/**
 * /annotation/:id — work through a queue one item at a time: the result on the
 * left, the queue's scores on the right. "Complete + next" saves the review
 * (shared: one per item, whoever completes it) and moves on; "Skip" only moves
 * on, in this tab; "Previous" goes back with the answers kept. It opens on the
 * first item nobody has completed. Shortcuts: ⌘/Ctrl+Enter completes, S skips,
 * ← goes back. The owner can share the queue with more reviewers. The Items
 * tab (`?view=items`) lists every item with its status; a click reviews it.
 * An archived queue is read-only: its items and reviews, a banner to restore.
 */

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { UserPlus } from "lucide-react";
import { Box } from "@/components/ui/box";
import { Button, ButtonLink } from "@/components/ui/button";
import { Crumbs, PageHeader, Progress } from "@/components/ui/page";
import { EmptyState, ErrorState, Skeleton, errorText } from "@/components/ui/states";
import { Tabs } from "@/components/ui/tabs";
import { useCurrentUser } from "@/features/auth/hooks";
import { useProject } from "@/features/projects/hooks";
import { ApiError } from "@/lib/api";
import { plural } from "@/lib/format";
import { useUrlState } from "@/lib/use-url-state";
import { useUiStore } from "@/stores/ui-store";
import { useCompleteItem, useQueue } from "./hooks";
import { ItemView } from "./item-view";
import { QueueItems } from "./queue-items";
import { Reviewers } from "./queues-tab";
import { ReviewPanel, type Draft } from "./review-panel";
import { sameValue } from "./scores";
import { ShareDialog } from "./share-dialog";
import type { QueueDetail, QueueItem } from "./types";
import { ArchiveMenu } from "@/features/archive/archive-menu";
import { ArchivedBadge, ArchivedBanner } from "@/features/archive/archived";
import { useRestore } from "@/features/archive/hooks";
import { isArchived } from "@/features/archive/kinds";

const back = <ButtonLink to="/annotation">Back to queues</ButtonLink>;

export function AnnotationQueuePage() {
  const { id = "" } = useParams();
  const queue = useQueue(id);
  if (queue.isPending) return <Skeleton label="Loading queue" />;
  if (queue.error instanceof ApiError && queue.error.status === 404) {
    return (
      <EmptyState title="No such queue" action={back}>
        This queue does not exist, or it was not shared with you.
      </EmptyState>
    );
  }
  if (queue.isError) return <ErrorState error={queue.error} what="this queue" />;
  // Keyed, so opening another queue starts its own position and answers.
  return <QueueWork key={queue.data.id} queue={queue.data} />;
}

/** Typing in a field should not trigger the single-key shortcuts. */
const isTyping = (target: EventTarget | null) =>
  target instanceof HTMLElement &&
  (target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName));

/** A completed item opens with its saved review; a pending one empty. */
const savedDraft = (it: QueueItem): Draft => ({
  answers: it.answers,
  comment: it.comment,
  corrected: it.corrected_output ?? null,
});

function QueueWork({ queue }: { queue: QueueDetail }) {
  const items = queue.items;
  const total = items.length;
  const [index, setIndex] = useState(() => {
    const first = items.findIndex((it) => it.status === "pending");
    return first === -1 ? total : first;
  });
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [sharing, setSharing] = useState(false);
  const url = useUrlState();
  const { can } = useProject();
  const archived = isArchived(queue);
  const restore = useRestore("queue");
  const view = !can("annotations:review") || archived || url.get("view") === "items" ? "items" : "review";
  const open = (i: number) => (archived ? undefined : (setIndex(i), url.set({ view: null })));
  const me = useCurrentUser().data?.email ?? "";
  // Its creator changes a queue, or anyone holding annotations:manage.
  const isOwner =
    can("annotations:write") &&
    (can("annotations:manage") || me.toLowerCase() === queue.created_by.toLowerCase());
  const complete = useCompleteItem(queue.id);
  const notify = useUiStore((s) => s.notify);
  const item = items[index];
  const draft = item ? (drafts[item.id] ?? savedDraft(item)) : undefined;
  const completed = items.filter((it) => it.status === "completed").length;
  const skippedLeft = items.filter((it) => it.status === "pending" && skipped.has(it.id)).length;
  const ready = !!item && !!draft && queue.scores.every((s) => draft.answers[s.id]);

  const skip = () => {
    if (!item) return;
    setSkipped((s) => new Set(s).add(item.id));
    setIndex((i) => i + 1);
  };
  const submit = () => {
    if (!item || !draft || !ready || complete.isPending) return;
    complete.mutate(
      {
        itemId: item.id,
        answers: draft.answers,
        comment: draft.comment,
        // Opened but left as the agent said it, or left blank: no correction.
        corrected_output:
          draft.corrected === "" || sameValue(draft.corrected, item.output) ? null : draft.corrected,
      },
      {
        onSuccess: () => setIndex((i) => i + 1),
        onError: (e) => notify(errorText(e, "Could not save the review."), "error"),
      },
    );
  };
  const previous = () => setIndex((i) => Math.max(0, i - 1));

  // Shortcuts read the latest handlers through a ref, so the listener is added once.
  const keys = useRef({ submit, skip, previous, view });
  keys.current = { submit, skip, previous, view };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (keys.current.view !== "review") return;
      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        keys.current.submit();
      } else if (!isTyping(e.target) && !e.metaKey && !e.ctrlKey && !e.altKey) {
        if (e.key === "s" || e.key === "S") keys.current.skip();
        else if (e.key === "ArrowLeft") keys.current.previous();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <>
      <div className="flex flex-col gap-2.5">
        <Crumbs items={[{ label: "Annotate", to: "/annotation" }, { label: queue.name }]} />
        <PageHeader
          title={queue.name}
          badges={archived && <ArchivedBadge />}
          subtitle={queue.description}
          actions={
            <div className="flex items-center gap-4">
              <Reviewers owner={queue.created_by} reviewers={queue.reviewers} />
              {isOwner && !archived && (
                <Button onClick={() => setSharing(true)}>
                  <UserPlus size={14} strokeWidth={2} aria-hidden />
                  Share
                </Button>
              )}
              {total > 0 && (
                <div className="flex w-48 flex-col gap-1.5">
                  <span className="flex justify-between text-xs text-muted">
                    <span>Item</span>
                    <span className="font-mono text-ink">
                      {Math.min(index + 1, total)} / {total}
                    </span>
                  </span>
                  <Progress value={completed} max={total} label="Items completed" />
                </div>
              )}
              {!archived && <ArchiveMenu kind="queue" row={queue} size="md" />}
            </div>
          }
        />
      </div>
      <ArchivedBanner kind="queue" row={queue} mayRestore={isOwner} onRestore={() => void restore(queue)} />
      {total > 0 && !archived && (
        <Tabs
          label="Queue"
          value={view}
          onChange={(v) => url.set({ view: v === "items" ? v : null })}
          items={[
            { key: "review", label: "Review" },
            { key: "items", label: "Items", count: total },
          ]}
        />
      )}
      {total === 0 ? (
        <Box>
          <EmptyState title="No items in this queue yet" action={back}>
            Add items from an experiment's results: tick them, then "Send to annotation".
          </EmptyState>
        </Box>
      ) : view === "items" ? (
        <QueueItems items={items} scores={queue.scores} skipped={skipped} current={index} onOpen={open} />
      ) : !item || !draft ? (
        <Box>
          <EmptyState
            title="Queue finished"
            action={
              <div className="flex gap-2">
                <Button onClick={previous}>Review last item</Button>
                <Button onClick={() => url.set({ view: "items" })}>See all items</Button>
                {back}
              </div>
            }
          >
            {`${plural(completed, "item")} completed${skippedLeft ? `, ${skippedLeft} skipped` : ""} of ${total}.`}
          </EmptyState>
        </Box>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-[minmax(0,1fr)_380px]">
          <ItemView key={item.id} item={item} />
          <ReviewPanel
            item={item}
            scores={queue.scores}
            draft={draft}
            onDraft={(patch) => setDrafts((d) => ({ ...d, [item.id]: { ...draft, ...patch } }))}
            canGoBack={index > 0}
            saving={complete.isPending}
            onPrevious={previous}
            onSkip={skip}
            onComplete={submit}
          />
        </div>
      )}
      {sharing && <ShareDialog queue={queue} onClose={() => setSharing(false)} />}
    </>
  );
}
